import {
  Alert,
  AlertTitle,
  Snackbar,
  SnackbarProps,
  Typography,
  useTheme,
} from "@mui/material";
import { createContext, useMemo, useState } from "react";

const DEFAULT_AUTO_HIDE_DURATION = 6 * 1000;

export type SnackbarSeverity = "warning" | "info" | "success" | "error";

export type ShowSnackbarOptions = {
  message: string;
  severity: SnackbarSeverity;
  title?: string;
  /**
   * Duration in ms before the toast auto-dismisses. Defaults to 6s.
   * Pass `Infinity` to disable auto-dismiss and require the user to close it manually.
   */
  autoHideDuration?: number;
};

export type SnackbarContext = {
  showSnackbar: (options: ShowSnackbarOptions) => void;
};

export const SnackbarContext = createContext<SnackbarContext | undefined>(
  undefined,
);

type SnackbarMessage = { key: number } & ShowSnackbarOptions;

type Props = {
  children: React.ReactNode | React.ReactNode[];
  anchorOrigin?: SnackbarProps["anchorOrigin"];
};

export function SnackbarProvider({ children, anchorOrigin }: Props) {
  const theme = useTheme();

  const [options, setOptions] = useState<SnackbarMessage | undefined>(
    undefined,
  );

  const contextValue = useMemo(
    () => ({
      showSnackbar: ({
        message,
        severity,
        title,
        autoHideDuration = DEFAULT_AUTO_HIDE_DURATION,
      }: ShowSnackbarOptions) => {
        setOptions({
          message,
          severity,
          title,
          key: Date.now(),
          autoHideDuration: Number.isFinite(autoHideDuration)
            ? autoHideDuration
            : undefined,
        });
      },
    }),
    [],
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        key={options?.key}
        open={!!options}
        autoHideDuration={options?.autoHideDuration ?? null}
        onClose={(_, reason) => {
          if (reason === "timeout") {
            setOptions(undefined);
          }
        }}
        anchorOrigin={anchorOrigin}
        sx={{
          minWidth: "30%",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 4.5,
        }}
      >
        {options && (
          <Alert
            onClose={() => setOptions(undefined)}
            severity={options.severity}
            data-testid={`toast-${options.severity}`}
            sx={{
              width: "100%",
            }}
          >
            {options.title && <AlertTitle>{options.title}</AlertTitle>}
            <Typography sx={{ whiteSpace: "pre-line" }}>
              {options.message}
            </Typography>
          </Alert>
        )}
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
